import { supabase, handleSupabaseError } from '@/lib/supabase';
import { WithdrawalRequest } from '@/types';
import { WithdrawalMethod, WithdrawalStatus, EWalletProvider } from '@/types/enums';

export interface CreateWithdrawalData {
  nasabahProfileId: string;
  method: WithdrawalMethod;
  amountRequested: number;
  ewalletProvider?: EWalletProvider;
  ewalletAccount?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankAccountName?: string;
}

export interface ApproveWithdrawalData {
  withdrawalId: string;
  approvedBy: string;
  evidenceFile?: File;
}

export interface WithdrawalRequestDetail {
  id: string;
  nasabahProfileId: string;
  nasabahName: string;
  accountNumber: string;
  method: WithdrawalMethod;
  ewalletProvider: EWalletProvider | null;
  ewalletAccount: string | null;
  fee: number;
  amountRequested: number;
  totalAmount: number;
  status: WithdrawalStatus;
  createdAt: Date;
}

class WithdrawalService {
  /**
   * Get withdrawal requests for a nasabah
   */
  async getWithdrawalRequests(
    nasabahProfileId: string
  ): Promise<{ data: WithdrawalRequest[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .eq('nasabah_profile_id', nasabahProfileId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: data.map(this.mapWithdrawalRequest),
        error: null
      };
    } catch (error) {
      return {
        data: [],
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Get pending withdrawal requests for pengelola
   */
  async getPendingWithdrawals(
    branchId: string
  ): Promise<{ data: WithdrawalRequest[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select(`
          *,
          nasabah_profiles!inner(branch_id)
        `)
        .eq('nasabah_profiles.branch_id', branchId)
        .eq('status', WithdrawalStatus.PENDING)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return {
        data: data.map(this.mapWithdrawalRequest),
        error: null
      };
    } catch (error) {
      return {
        data: [],
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Create new withdrawal request
   */
  async createWithdrawal(
    data: CreateWithdrawalData
  ): Promise<{ data: WithdrawalRequest | null; error: string | null }> {
    try {
      // Check nasabah balance
      const { data: profileData } = await supabase
        .from('nasabah_profiles')
        .select('balance')
        .eq('id', data.nasabahProfileId)
        .single();

      if (!profileData) throw new Error('Profil tidak ditemukan');
      if (profileData.balance < data.amountRequested) {
        throw new Error('Saldo tidak mencukupi');
      }

      // Calculate fee (example: 0 for cash, 2500 for e-wallet)
      const fee = data.method === WithdrawalMethod.CASH ? 0 : 2500;

      // Create withdrawal request
      const { data: withdrawalData, error: withdrawalError } = await supabase
        .from('withdrawal_requests')
        .insert({
          nasabah_profile_id: data.nasabahProfileId,
          method: data.method,
          ewallet_provider: data.ewalletProvider,
          ewallet_account: data.ewalletAccount,
          bank_name: data.bankName,
          bank_account_number: data.bankAccountNumber,
          bank_account_name: data.bankAccountName,
          amount_requested: data.amountRequested,
          fee: fee,
          status: WithdrawalStatus.PENDING
        })
        .select()
        .single();

      if (withdrawalError) throw withdrawalError;

      return {
        data: this.mapWithdrawalRequest(withdrawalData),
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: handleSupabaseError(error)
      };
    }
  }

  /**
   * Approve withdrawal request
   */
  async approveWithdrawal(
    data: ApproveWithdrawalData
  ): Promise<{ error: string | null }> {
    try {
      // Get withdrawal request
      const { data: withdrawalData } = await supabase
        .from('withdrawal_requests')
        .select('*, nasabah_profiles(balance)')
        .eq('id', data.withdrawalId)
        .single();

      if (!withdrawalData) throw new Error('Permintaan penarikan tidak ditemukan');

      const totalAmount = withdrawalData.amount_requested + withdrawalData.fee;

      // Check balance again
      if (withdrawalData.nasabah_profiles.balance < totalAmount) {
        throw new Error('Saldo nasabah tidak mencukupi');
      }

      // Update withdrawal status
      const { error: updateError } = await supabase
        .from('withdrawal_requests')
        .update({
          status: WithdrawalStatus.APPROVED,
          approved_by: data.approvedBy,
          approved_at: new Date().toISOString()
        })
        .eq('id', data.withdrawalId);

      if (updateError) throw updateError;

      // Deduct balance
      const { error: balanceError } = await supabase.rpc('update_balance', {
        p_nasabah_profile_id: withdrawalData.nasabah_profile_id,
        p_amount: -totalAmount
      });

      if (balanceError) throw balanceError;

      // Create transaction record
      const { data: profileData } = await supabase
        .from('nasabah_profiles')
        .select('branch_id')
        .eq('id', withdrawalData.nasabah_profile_id)
        .single();

      await supabase.from('transactions').insert({
        branch_id: profileData?.branch_id,
        nasabah_profile_id: withdrawalData.nasabah_profile_id,
        type: 'KREDIT',
        category_name: 'Penarikan Saldo',
        amount_locked: totalAmount,
        notes: `Penarikan via ${withdrawalData.method}`,
        created_by: data.approvedBy
      });

      // Upload evidence if provided
      if (data.evidenceFile) {
        const evidenceUrl = await this.uploadEvidence(data.evidenceFile, data.withdrawalId);
        
        await supabase.from('evidences').insert({
          withdrawal_request_id: data.withdrawalId,
          file_url: evidenceUrl,
          file_type: data.evidenceFile.type
        });
      }

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Reject withdrawal request
   */
  async rejectWithdrawal(
    withdrawalId: string,
    rejectedBy: string,
    reason: string
  ): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase
        .from('withdrawal_requests')
        .update({
          status: WithdrawalStatus.REJECTED,
          approved_by: rejectedBy,
          approved_at: new Date().toISOString(),
          rejection_reason: reason
        })
        .eq('id', withdrawalId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Get pending withdrawals with nasabah name — for the approval page
   */
  async getPendingWithdrawalsForApproval(
    branchId: string
  ): Promise<{ data: WithdrawalRequestDetail[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select(`
          id,
          nasabah_profile_id,
          method,
          ewallet_provider,
          ewallet_account,
          amount_requested,
          fee,
          status,
          created_at,
          nasabah_profiles!inner(
            id,
            branch_id,
            account_number,
            users!nasabah_profiles_user_id_fkey(full_name)
          )
        `)
        .eq('nasabah_profiles.branch_id', branchId)
        .eq('status', WithdrawalStatus.PENDING)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return {
        data: (data || []).map((row: any) => ({
          id: row.id,
          nasabahProfileId: row.nasabah_profile_id,
          nasabahName: row.nasabah_profiles?.users?.full_name ?? '—',
          accountNumber: row.nasabah_profiles?.account_number ?? '—',
          method: row.method as WithdrawalMethod,
          ewalletProvider: row.ewallet_provider as EWalletProvider | null,
          ewalletAccount: row.ewallet_account,
          fee: parseFloat(row.fee),
          amountRequested: parseFloat(row.amount_requested),
          totalAmount: parseFloat(row.amount_requested) + parseFloat(row.fee),
          status: row.status as WithdrawalStatus,
          createdAt: new Date(row.created_at),
        })),
        error: null,
      };
    } catch (error) {
      return { data: [], error: handleSupabaseError(error) };
    }
  }

  /**
   * Upload evidence file to storage
   */
  private async uploadEvidence(file: File, withdrawalId: string): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${withdrawalId}-${Date.now()}.${fileExt}`;
    const filePath = `evidences/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('withdrawal-evidences')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('withdrawal-evidences')
      .getPublicUrl(filePath);

    return data.publicUrl;
  }

  /**
   * Map database record to WithdrawalRequest type
   */
  private mapWithdrawalRequest(data: any): WithdrawalRequest {
    return {
      id: data.id,
      nasabahProfileId: data.nasabah_profile_id,
      method: data.method as WithdrawalMethod,
      ewalletProvider: data.ewallet_provider as EWalletProvider | null,
      ewalletAccount: data.ewallet_account,
      fee: parseFloat(data.fee),
      amountRequested: parseFloat(data.amount_requested),
      status: data.status as WithdrawalStatus,
      approvedBy: data.approved_by,
      approvedAt: data.approved_at ? new Date(data.approved_at) : null,
      evidenceUrl: data.evidence_url,
      createdAt: new Date(data.created_at)
    };
  }
}

export const withdrawalService = new WithdrawalService();
